import { createContext, useContext, useEffect } from "react";

/**
 * The phone's navigation drawer, as far as anything inside it needs to know.
 *
 * One thing only: a dialog opening closes it. The drawer holds the product's
 * sidebar, and the sidebar opens dialogs -- the Helpdesk's "+" raises a ticket
 * from it -- which then drew over the screen with the drawer still open
 * between them, so closing the dialog put you back in a menu you had finished
 * with rather than on the page.
 *
 * Signalled by the dialog rather than by the button that opened it, because
 * there are many such buttons in several products' sidebars and one `Overlay`,
 * and a rule every button has to remember is one the next button forgets.
 *
 * Null outside the frame, where there is no drawer to close.
 */
export const NavDrawerContext = createContext<{ close: () => void } | null>(null);

/** Close the drawer, if there is one, when the calling component mounts. */
export function useCloseNavDrawerOnMount(): void {
  const drawer = useContext(NavDrawerContext);

  useEffect(() => {
    drawer?.close();
    // Mount only: a dialog that re-rendered must not keep shutting a drawer
    // somebody has since reopened behind it.
  }, []);
}
